/**
 * creature/templates/pony.ts — The Gentle Steed
 *
 * Ponies are shorter, stouter, and friendlier than horses. They make
 * excellent first mounts — forgiving of mistakes, patient with beginners.
 * Their movement is bouncier and their ears are proportionally larger.
 */

import type { SpeciesTemplate } from "../species.js";
import { registerSpecies, createBreed } from "../species.js";
import { SPECIES_HORSE } from "./horse.js";

// ─── Pony (derived from Horse) ──────────────────────────────────────────────

export const SPECIES_PONY: SpeciesTemplate = createBreed(SPECIES_HORSE, {
  id: "pony_standard",
  displayName: "Pony",
  size: "medium",
  baseSpeed: 2.0,
  heightAtShoulder: 1.0,
  riderOffsetZ: 0.7,
  palette: {
    ...SPECIES_HORSE.palette,
    body: "#b08050",
    markings: "#907040",
    belly: "#c89868",
  },
  bodyShape: {
    ...SPECIES_HORSE.bodyShape,
    bodyLength: 15,
    bodyWidth: 5.0,
    headRadius: 4.5,
    earSize: 1.1,
    legLength: 1.2,
    neckLength: 1.4,
    tailLength: 16,
  },
  behaviorDefaults: {
    ...SPECIES_HORSE.behaviorDefaults,
    sociability: 0.7,
    courage: 0.5,
    obedience: 0.75,
    playfulness: 0.5,
    stamina: 0.7,
  },
});

export const SPECIES_PONY_SHETLAND = createBreed(SPECIES_PONY, {
  id: "pony_shetland",
  displayName: "Shetland Pony",
  size: "small",
  baseSpeed: 1.8,
  heightAtShoulder: 0.8,
  riderOffsetZ: 0.5,
  palette: {
    ...SPECIES_PONY.palette,
    body: "#3a2a1a",
    markings: "#2a1a0a",
    belly: "#4a3a2a",
  },
  bodyShape: {
    ...SPECIES_PONY.bodyShape,
    bodyLength: 13,
    bodyWidth: 5.5,      // stout
    legLength: 0.9,      // short legs
    headRadius: 4.2,
  },
  behaviorDefaults: {
    ...SPECIES_PONY.behaviorDefaults,
    courage: 0.7,
    stamina: 0.8,
    obedience: 0.5,      // shetlands have a reputation
  },
});

export const SPECIES_PONY_UNICORN = createBreed(SPECIES_PONY, {
  id: "pony_unicorn",
  displayName: "Unicorn",
  palette: {
    ...SPECIES_PONY.palette,
    body: "#e8e0f0",
    markings: "#d0c0e8",
    eyes: "#a070d0",
    belly: "#f0eaf8",
    accent: "#c8b0e0",
  },
  bodyShape: {
    ...SPECIES_PONY.bodyShape,
    legLength: 1.4,
    neckLength: 1.8,
  },
  behaviorDefaults: {
    ...SPECIES_PONY.behaviorDefaults,
    courage: 0.3,
    curiosity: 0.8,
    sociability: 0.3,    // shy and elusive
  },
  moodContribution: {
    warmth: 0.05,
    coziness: 0.06,
    energy: 0.04,
    nature: 0.15,
  },
});

// ─── Register ───────────────────────────────────────────────────────────────

export function registerPonies(): void {
  registerSpecies(SPECIES_PONY);
  registerSpecies(SPECIES_PONY_SHETLAND);
  registerSpecies(SPECIES_PONY_UNICORN);
}
